import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { LogOut, X } from "Shared/appIcons";
import { BrandMark } from "Shared/Layouts/components/BrandMark";
import {
	NavButton,
	NavGroup,
	type NavItem,
} from "Shared/Layouts/components/NavButton";
import { UserIdentity } from "Shared/Layouts/components/UserIdentity";
import { SimpleTooltip } from "Shared/SimpleTooltip";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type Props = {
	open: boolean;
	onClose: () => void;
	navItems: NavItem[];
	isActive: (to?: string) => boolean;
	userLogin: string;
	userName: string;
	tenantName: string;
	userInitials: string;
	onSignOut: () => void;
};

/**
 * Off-canvas drawer navigation: hidden on `md` and up where `TopbarNav`
 * takes over. The trigger (hamburger) lives in `DrawerTopbar`.
 */
export const DrawerNav = ({
	open,
	onClose,
	navItems,
	isActive,
	userLogin,
	userName,
	tenantName,
	userInitials,
	onSignOut,
}: Props) => {
	const { t } = useResource();
	const top = navItems.filter((i) => i.group === "top");
	const bottom = navItems.filter((i) => i.group === "bottom");
	const hasBottom = bottom.length > 0;

	return (
		<>
			{open ? (
				<div
					aria-hidden="true"
					onClick={onClose}
					className="bg-foreground/40 fixed inset-0 z-30 backdrop-blur-sm md:hidden"
				/>
			) : null}

			<aside
				className={cn(
					"border-sidebar-border bg-sidebar text-sidebar-foreground fixed inset-y-0 left-0 z-40 flex w-60 flex-col border-r transition-transform duration-200 ease-out md:hidden",
					open ? "translate-x-0" : "-translate-x-full",
				)}
			>
				<div className="border-border flex h-14 items-center justify-between border-b pr-2 pl-5">
					<div className="flex min-w-0 flex-1 items-center">
						<BrandMark textClassName="ml-2.5 flex overflow-hidden" />
					</div>
					<SimpleTooltip content={t(Resources.Common.Close)}>
						<Button
							variant="ghost"
							size="icon-sm"
							onClick={onClose}
							aria-label={t(Resources.Common.Close)}
							className="text-muted-foreground hover:text-foreground flex-shrink-0"
						>
							<X className="h-4 w-4" />
						</Button>
					</SimpleTooltip>
				</div>

				<nav className="text-body flex-1 overflow-x-hidden overflow-y-auto px-2 py-3">
					<NavGroup>
						{top.map((item) => (
							<NavButton
								key={item.key}
								item={item}
								active={isActive(item.to)}
								collapsed={false}
							/>
						))}
					</NavGroup>

					{hasBottom ? (
						<div className="mt-6">
							<NavGroup>
								{bottom.map((item) => (
									<NavButton
										key={item.key}
										item={item}
										active={isActive(item.to)}
										collapsed={false}
									/>
								))}
							</NavGroup>
						</div>
					) : null}
				</nav>

				<div className="border-border flex items-center gap-2.5 border-t px-4 py-3">
					<UserIdentity
						initials={userInitials}
						name={userName}
						tenantName={tenantName}
						avatarTooltip={userLogin}
						size="md"
						textClassName="flex-1 overflow-hidden"
					/>
					<SimpleTooltip content={t(Resources.Common.SignOut)}>
						<Button
							variant="ghost"
							size="icon-sm"
							onClick={onSignOut}
							aria-label={t(Resources.Common.SignOut)}
							className="text-muted-foreground hover:text-foreground flex-shrink-0"
						>
							<LogOut className="h-4 w-4" />
						</Button>
					</SimpleTooltip>
				</div>
			</aside>
		</>
	);
};
